/**
 * Copyright 2021-2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { DetailAccordionCard, DetailName, Identification, labels } from '@umati/dashboard-common';
import { IMachine, IMachineData, IPositionFair } from '@umati/dashboard-types';
import SectionAccordionCard from 'components/detail/template/SectionAccordionCard';
import React from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { connect } from 'react-redux';
import FrontendURLs from 'utilities/FrontendURLs';
import MachineHandling from 'utilities/MachineHandling';
import TemplateHelper from 'utilities/TemplateHelper';
import ImageAndTimeline from '../components/detail/common/ImageAndTimeline';
import Header from '../components/header/header';
import { DInfoRects } from '../components/_lib/Details';
import { getMachine, getMachines } from '../redux/selectors';
import SectionData from 'components/machine/sectionData';
import { useSearchParams } from 'react-router-dom';

interface ITemplateTypeDetailProps {
  machine?: IMachine;
  machines: IMachine[];
}

interface IRoutingParameters {
  machineId: string;
  typeId: string;
  fairTag?: string;
}

interface ITemplateTypeBase extends IRoutingParameters {
  data?: any;
}

interface IProps extends ITemplateTypeBase, ITemplateTypeDetailProps {}

const TemplateTypeDetail = (props: IProps) => {
  const machineId = props.machineId;

  const machineDetails = props.data;
  const ident = props.machine?.Data as IMachineData | undefined;
  const productInstanceUri = MachineHandling.getProductInstanceUri(props.machine);
  const machineModel = MachineHandling.getMachineModel(props.machine);

  const template = TemplateHelper.getTemplateByTypeId(props.typeId);

  const instanceUri = template.header.nameShowStaticText
    ? template.header.nameStaticText || 'undefined'
    : !machineDetails
      ? productInstanceUri || 'undefined'
      : TemplateHelper.getVariableValue(machineDetails, template.header.nameVariable, productInstanceUri);
  const model = template.header.modelShowStaticText
    ? template.header.modelStaticText || 'undefined'
    : !machineDetails
      ? machineModel || 'undefined'
      : TemplateHelper.getVariableValue(machineDetails, template.header.modelVariable, machineModel);
  const position = props.fairTag ? (props.machine?.Positions?.[props.fairTag] as IPositionFair) : null;
  const detailRectProps = [
    { label: labels.hall, value: position?.hall },
    { label: labels.booth, value: position?.booth },
  ];
  const anchor = model.replace(/\s+/g, '');
  const headerDetail = {
    text: model,
    link: FrontendURLs.getMachine(
      { TypeDefinition: props.typeId, MachineId: props.machineId, Encoding: props.machine?.Encoding || 2 },
      props.fairTag,
    ),
    anchor,
  };

  const defaultImagePathClassifier = template.imagesConfig.defaultImagePathClassifierVariable
    ? TemplateHelper.getVariableValue(machineDetails, template.imagesConfig.defaultImagePathClassifierVariable)
    : undefined;

  const [searchParams] = useSearchParams();
  const showRawData = searchParams.get('showRawData') === 'true' ? true : false;

  return (
    <div>
      {props.fairTag && (
        <Header
          {...props}
          fairTag={props.fairTag}
          detail={headerDetail}
          containerClass="container-max"
          listType="machines"
        />
      )}
      <div className="view-machine-detail">
        <div className="hero">
          <div className="container-max">
            <DetailName isOnline={props?.machine?.Online} name={instanceUri} model={model} />
            <DInfoRects infos={detailRectProps} />
          </div>
        </div>
        <div className="container-max">
          <ImageAndTimeline
            machineId={machineId}
            imagePath={props.machine?.ImagePath!}
            defaultImagePathClassifier={defaultImagePathClassifier}
            lamps={TemplateHelper.getStacklightLamps(machineDetails, template.stacklight)}
            hideTimeline={template.timeline === null || template.timeline === undefined}
            timelineColorMap={TemplateHelper.getTimelineColorMap(template.timeline)}
            machineType={props.typeId}
          />
          <div className="row justify-content-end">
            <div className="col-12 offset-lg-2 col-lg-9">
              {!machineDetails ? (
                <Accordion defaultActiveKey="0">
                  <DetailAccordionCard eventKey="0" title="Identification" isExpanded={true}>
                    <Identification {...ident}></Identification>
                  </DetailAccordionCard>
                </Accordion>
              ) : (
                <>
                  {template.sections.map((section, sectionIndex) => (
                    <Accordion key={sectionIndex} defaultActiveKey={TemplateHelper.getKeySection(sectionIndex) + ''}>
                      {!section.isPlaceholder ? (
                        <SectionAccordionCard
                          key={TemplateHelper.getKeySection(sectionIndex)}
                          eventKey={TemplateHelper.getKeySection(sectionIndex) + ''}
                          sectionIndex={sectionIndex}
                          sectionDataIndex={0}
                          title={TemplateHelper.getTextValue(section.title)}
                          section={section}
                          data={machineDetails}
                          isExpanded={sectionIndex === 0}
                        />
                      ) : (
                        TemplateHelper.getPlaceholderObjects(machineDetails, section.placeholderObject).map(
                          (data, dataIndex) => (
                            <SectionAccordionCard
                              key={TemplateHelper.getKeySection(sectionIndex, dataIndex)}
                              eventKey={TemplateHelper.getKeySection(sectionIndex, dataIndex) + ''}
                              sectionIndex={sectionIndex}
                              sectionDataIndex={dataIndex}
                              title={TemplateHelper.getVariableValue(data, section.titleVariable)}
                              section={section}
                              data={data}
                              isExpanded={sectionIndex === 0 && dataIndex === 0}
                            />
                          ),
                        )
                      )}
                    </Accordion>
                  ))}
                </>
              )}
              {showRawData && (
                <Accordion defaultActiveKey="999">
                  <DetailAccordionCard eventKey="999" title="Data" isExpanded={true}>
                    <SectionData cols="col-12 col-sm-12 col-md-12">
                      <code>
                        <pre>{JSON.stringify(machineDetails, null, 2)}</pre>
                      </code>
                    </SectionData>
                  </DetailAccordionCard>
                </Accordion>
              )}
            </div>
            <div className="col-12 col-lg-1" />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps: ITemplateTypeBase): ITemplateTypeDetailProps => {
  const machineId = ownProps.machineId;
  return {
    machines: getMachines(state),
    machine: getMachine(state, machineId),
  };
};

export default connect(mapStateToProps)(TemplateTypeDetail);
